import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { ReductionProposal } from "legacyNext/types";

type Props = {
    open: boolean;
    handleClose: () => void;
    handleAddProposal: (proposal: Partial<ReductionProposal>) => void;
};

export const ReductionModal = (props: Props) => {
    const { open, handleClose, handleAddProposal } = props;

    const { t } = useTranslation();

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        const formData = new FormData(e.currentTarget);

        const proposal: Partial<ReductionProposal> = {
            title: formData.get("title") as string,
            description: (formData.get("description") as string) || "",
            investment: formData.get("investment")?.toString() ?? "",
            emissionSaving: Number(formData.get("emissionSaving")),
            energySaving: Number(formData.get("energySaving")),
            costSaving: Number(formData.get("costSaving")),
        };

        e.preventDefault();
        handleAddProposal(proposal);
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{ component: "form", onSubmit: handleSubmit }}
        >
            <DialogTitle sx={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                {t("legacyNext.createCustomProposal", "create custom proposal")}
            </DialogTitle>
            <DialogContent
                sx={{ display: "grid", gap: 2, minWidth: 500, py: 8 }}
            >
                <TextField
                    name="title"
                    sx={{ mt: 2 }}
                    size="small"
                    required
                    fullWidth
                    label={t("legacyNext.proposalTitle", "Proposal Title")}
                />
                <TextField
                    name="description"
                    fullWidth
                    size="small"
                    label={t(
                        "legacyNext.proposalDescription",
                        "Proposal Description"
                    )}
                    multiline
                    rows={2}
                />
                <TextField
                    name="investment"
                    size="small"
                    required
                    label={t("legacyNext.investment", "investment")}
                    type="number"
                    slotProps={{ input: { endAdornment: "kr." } }}
                />
                <TextField
                    name="emissionSaving"
                    size="small"
                    required
                    label={t(
                        "legacyNext.annualC02eReduction",
                        "annual CO2 reduction"
                    )}
                    type="number"
                    slotProps={{
                        input: { endAdornment: "kgCO₂" },
                    }}
                />
                <TextField
                    name="energySaving"
                    size="small"
                    required
                    label={t(
                        "legacyNext.annualEnergySavings",
                        "annual energy savings"
                    )}
                    type="number"
                    slotProps={{ input: { endAdornment: "kWh" } }}
                />
                <TextField
                    name="costSaving"
                    size="small"
                    required
                    label={t(
                        "legacyNext.annualCostSaving",
                        "annual cost savings"
                    )}
                    type="number"
                    slotProps={{ input: { endAdornment: "kr." } }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {t("labels.cancel", "cancel")}
                </Button>
                <Button type="submit">
                    {t("common.actions.save", "save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
